import { z } from "zod";
import { noAnswerSchema, type FieldDescriptor } from "./types.js";

/**
 * Sequential reference number, allocated **at submit** inside the submission transaction — never
 * at page load, because "page loads must not burn numbers" (docs/01-PRODUCT-SPEC.md §3.2).
 * Allocation itself is Phase 5's `services/identifiers.ts`; this is the configuration contract.
 */
const configSchema = z.object({
  prefix: z.string().max(20).default(""),
  suffix: z.string().max(20).default(""),
  counterStart: z.number().int().nonnegative().default(1),
  padding: z.number().int().min(0).max(12).default(4),
  resetScope: z.enum(["per_form_cycle", "per_form", "per_programme"]).default("per_form_cycle"),
});

export type UniqueIdConfig = z.infer<typeof configSchema>;

/** `{prefix}{zero-padded counter}{suffix}` → e.g. SS25 + 1001 + NG = "SS251001NG". */
export const formatUniqueId = (config: UniqueIdConfig, counter: number): string =>
  `${config.prefix}${String(counter).padStart(config.padding, "0")}${config.suffix}`;

export const uniqueIdField: FieldDescriptor<UniqueIdConfig> = {
  type: "unique_id",
  label: "Reference number",
  category: "identifier",
  icon: "Hash",
  configSchema,
  // Invisible in the form and never accepted from the client — the server allocates it.
  valueSchema: () => noAnswerSchema(),
  defaultConfig: {
    prefix: "",
    suffix: "",
    counterStart: 1,
    padding: 4,
    resetScope: "per_form_cycle",
  },
  supportsProfileBinding: false,
  defaultSearchable: true,
  renderHints: { isHiddenFromApplicant: true },
};
